import axios from 'axios';

const api = axios.create({
  baseURL: 'https://keepa.webaccess.nl',
  headers: { 'Content-Type': 'application/json' },
});

// Global callback so AuthContext can register its refreshUser fn with the interceptor
let _onPermissionChanged: (() => Promise<void>) | null = null;
export const setPermissionRefreshCallback = (fn: () => Promise<void>) => {
  _onPermissionChanged = fn;
};

// Attach JWT token + active org to all requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  const orgId = localStorage.getItem('active_org_id');
  if (orgId) {
    config.headers['X-Organization-ID'] = orgId;
  }
  return config;
});

// Auto-refresh token on 401; re-fetch permissions on 403
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      const refreshToken = localStorage.getItem('refresh_token');
      if (refreshToken) {
        try {
          const res = await axios.post(`${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/v1/auth/refresh`, {
            refresh_token: refreshToken,
          });
          localStorage.setItem('access_token', res.data.access_token);
          localStorage.setItem('refresh_token', res.data.refresh_token);
          originalRequest.headers.Authorization = `Bearer ${res.data.access_token}`;
          return api(originalRequest);
        } catch {
          localStorage.removeItem('access_token');
          localStorage.removeItem('refresh_token');
          localStorage.removeItem('active_org_id');
          window.location.reload();
        }
      }
    }

    // On 403 (permission denied — role may have changed), refresh user permissions
    if (error.response?.status === 403 && !originalRequest._permRetry) {
      originalRequest._permRetry = true;
      if (_onPermissionChanged) {
        await _onPermissionChanged();
        // Retry the original request once with refreshed permissions
        return api(originalRequest);
      }
    }

    return Promise.reject(error);
  }
);

// ── Products ──────────────────────────────────────────────────────────────────
export const getProducts = (params?: Record<string, any>) =>
  api.get('/api/v1/products', { params });

export const createProduct = (data: any) =>
  api.post('/api/v1/products', data);

export const getProduct = (id: string, params?: Record<string, any>) =>
  api.get(`/api/v1/products/${id}`, { params });

export const updateProduct = (id: string, data: any) =>
  api.put(`/api/v1/products/${id}`, data);

export const deleteProduct = (id: string) =>
  api.delete(`/api/v1/products/${id}`);

export const bulkDeleteProducts = (productIds: string[]) =>
  api.post('/api/v1/products/bulk-delete', { product_ids: productIds });

export const deleteAllProducts = (params?: Record<string, any>) =>
  api.delete('/api/v1/products', { params });

// ── Keepa Search ──────────────────────────────────────────────────────────────
export const searchKeepa = (query: string, marketplace_code: string) =>
  api.post('/api/v1/keepa/search', { query, marketplace_code });

export const getMarketplaces = () =>
  api.get('/api/v1/keepa/marketplaces');

// ── Mappings ──────────────────────────────────────────────────────────────────
export const getMappings = (productId: string) =>
  api.get(`/api/v1/products/${productId}/mappings`);

export const createMapping = (productId: string, data: any) =>
  api.post(`/api/v1/products/${productId}/mappings`, data);

export const updateMapping = (mappingId: string, data: any) =>
  api.put(`/api/v1/mappings/${mappingId}`, data);

export const deleteMapping = (mappingId: string) =>
  api.delete(`/api/v1/mappings/${mappingId}`);

export const fetchVariantRanks = (mappingId: string) =>
  api.post(`/api/v1/mappings/${mappingId}/fetch-variant-ranks`);

// ── Matching ──────────────────────────────────────────────────────────────────
export const triggerMatch = (productId: string) =>
  api.post(`/api/v1/products/${productId}/match`);

export const getCandidates = (productId: string) =>
  api.get(`/api/v1/products/${productId}/candidates`);

export const confirmCandidate = (candidateId: string) =>
  api.post(`/api/v1/candidates/${candidateId}/confirm`);

export const rejectCandidate = (candidateId: string) =>
  api.post(`/api/v1/candidates/${candidateId}/reject`);

export const triggerBulkMatch = () =>
  api.post('/api/v1/matching/bulk-run');

// ── Keepa Pull ────────────────────────────────────────────────────────────────
export const startKeepaPull = (marketplace_code?: string, status?: string, product_ids?: string[]) => {
  const params: Record<string, string> = {};
  if (marketplace_code) params.marketplace_code = marketplace_code;
  if (status) params.status = status;
  return api.post('/api/v1/products/start-keepa-pull', product_ids && product_ids.length > 0 ? product_ids : null, { params });
};

export const runUnmatched = (marketplace_code?: string, product_ids?: string[]) => {
  const params: Record<string, string> = {};
  if (marketplace_code) params.marketplace_code = marketplace_code;
  return api.post('/api/v1/products/run-unmatched', product_ids && product_ids.length > 0 ? product_ids : null, { params });
};

// ── Bulk Import ───────────────────────────────────────────────────────────────
export const bulkImportProducts = (data: string, format: string, marketplace_code?: string) => {
  const params: Record<string, string> = {};
  if (marketplace_code) params.marketplace_code = marketplace_code;
  return api.post('/api/v1/products/bulk-import', { data, format }, { params });
};

// ── Job Progress ──────────────────────────────────────────────────────────────
export const getJobProgress = (jobId: string) =>
  api.get(`/api/v1/jobs/${jobId}`);

export const cancelJob = (jobId: string) =>
  api.post(`/api/v1/jobs/${jobId}/cancel`);

// ── Auth ──────────────────────────────────────────────────────────────────────
export const authRegister = (data: { email: string; full_name: string; password: string; invite_token?: string }) =>
  api.post('/api/v1/auth/register', data);

export const authLogin = (data: { email: string; password: string }) =>
  api.post('/api/v1/auth/login', data);

export const authRefresh = (refresh_token: string) =>
  api.post('/api/v1/auth/refresh', { refresh_token });

export const authGetMe = () =>
  api.get('/api/v1/auth/me');

export const authUpdateMe = (data: { full_name?: string; email?: string }) =>
  api.put('/api/v1/auth/me', data);

export const authChangePassword = (data: { old_password: string; new_password: string }) =>
  api.post('/api/v1/auth/change-password', data);

export const authForgotPassword = (email: string) =>
  api.post('/api/v1/auth/forgot-password', { email });

export const authResetPassword = (data: { token: string; new_password: string }) =>
  api.post('/api/v1/auth/reset-password', data);

// ── Pricing Formulas ──────────────────────────────────────────────────────────
export const getPricingFormulas = () =>
  api.get('/api/v1/settings/pricing-formulas');

export const upsertPricingFormula = (marketplace_code: string, formula: string) =>
  api.put(`/api/v1/settings/pricing-formulas/${marketplace_code}`, { formula });

export const deletePricingFormula = (marketplace_code: string) =>
  api.delete(`/api/v1/settings/pricing-formulas/${marketplace_code}`);

export const validatePricingFormula = (formula: string, sample_base_price: number, sample_cost_price?: number) =>
  api.post('/api/v1/settings/pricing-formulas/validate', { formula, sample_base_price, sample_cost_price });

// ── Organizations ─────────────────────────────────────────────────────────────
export const getMyOrganizations = () =>
  api.get('/api/v1/organizations/me');

export const createOrganization = (name: string) =>
  api.post('/api/v1/organizations', { name });

export const getOrganization = (orgId: string) =>
  api.get(`/api/v1/organizations/${orgId}`);

export const updateOrganization = (orgId: string, name: string) =>
  api.put(`/api/v1/organizations/${orgId}`, { name });

// ── User Management (admin-only) ──────────────────────────────────────────────
export const getUsers = () =>
  api.get('/api/v1/users');

export const createUser = (data: { email: string; full_name: string; password: string; role_id?: string; is_admin?: boolean }) =>
  api.post('/api/v1/users', data);

export const updateUser = (userId: string, data: { full_name?: string; email?: string; new_password?: string; role_id?: string | null; is_active?: boolean; is_admin?: boolean }) =>
  api.put(`/api/v1/users/${userId}`, data);

export const deactivateUser = (userId: string) =>
  api.delete(`/api/v1/users/${userId}`);

export const getInvite = (token: string) =>
  api.get(`/api/v1/invites/${token}`);

export const acceptInvite = (token: string) =>
  api.post(`/api/v1/invites/${token}/accept`);

// ── Roles (admin-only, global) ────────────────────────────────────────────────
export const getRoles = () =>
  api.get('/api/v1/roles');

export const createRole = (data: { name: string; description?: string; permissions: string[] }) =>
  api.post('/api/v1/roles', data);

export const updateRole = (roleId: string, data: { name?: string; description?: string; permissions?: string[] }) =>
  api.put(`/api/v1/roles/${roleId}`, data);

export const deleteRole = (roleId: string) =>
  api.delete(`/api/v1/roles/${roleId}`);

export const getPermissions = () =>
  api.get('/api/v1/permissions');

// ── User Preferences ─────────────────────────────────────────────────────────
export const getUserPreferences = () =>
  api.get('/api/v1/user/preferences');

export const updateUserPreferences = (preferences: Record<string, any>) =>
  api.put('/api/v1/user/preferences', { preferences });

export default api;